import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const TIERS = ["bronze", "silver", "gold", "platinum"];
const NUM_FIELDS = [["min_spent", "Spesa minima (€)"], ["min_points", "Punti minimi"], ["min_transactions", "Acquisti minimi"], ["purchased_within_days", "Acquisto negli ultimi N giorni"]];

const toRules = (f) => {
  const r = { tiers: f.tiers, status: f.status === "any" ? null : f.status };
  NUM_FIELDS.forEach(([k]) => { r[k] = f[k] === "" ? null : Number(f[k]); });
  return r;
};

export const SegmentDialog = ({ segment, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!segment) return setF(null);
    const r = segment.rules || {};
    setF({ name: segment.name || "", description: segment.description || "", tiers: r.tiers || [], status: r.status || "any",
      ...Object.fromEntries(NUM_FIELDS.map(([k]) => [k, r[k] ?? ""])) });
  }, [segment]);

  useEffect(() => {
    if (!f) return;
    const t = setTimeout(() => api.post("/segments/preview", toRules(f)).then((r) => setPreview(r.data.count)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [f]);

  const save = async () => {
    if (!f.name.trim()) return toast.error("Inserisci un nome");
    const body = { name: f.name, description: f.description, rules: toRules(f) };
    try {
      if (segment.id) await api.put(`/segments/${segment.id}`, body); else await api.post("/segments", body);
      toast.success("Segmento salvato");
      onClose(); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Dialog open={!!f} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="segment-dialog">
        <DialogHeader><DialogTitle className="font-heading">{segment?.id ? "Modifica segmento" : "Nuovo segmento"}</DialogTitle></DialogHeader>
        {f && (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="segment-name-input" value={f.name} onChange={set("name")} /></div>
            <div className="space-y-1.5"><Label>Descrizione</Label><Input data-testid="segment-description-input" value={f.description} onChange={set("description")} /></div>
            <div className="grid grid-cols-2 gap-3">
              {NUM_FIELDS.map(([k, l]) => (
                <div key={k} className="space-y-1.5"><Label className="text-xs">{l}</Label><Input type="number" min="0" data-testid={`segment-${k}-input`} value={f[k]} onChange={set(k)} /></div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label>Livelli</Label>
              <div className="flex flex-wrap gap-2">
                {TIERS.map((t) => {
                  const on = f.tiers.includes(t);
                  return <button key={t} type="button" data-testid={`segment-tier-${t}`} onClick={() => setF({ ...f, tiers: on ? f.tiers.filter((x) => x !== t) : [...f.tiers, t] })}
                    className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${on ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{t}</button>;
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="segment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="any">Qualsiasi</SelectItem><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="inattivo">Inattivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="segment-preview-count">
              Anteprima: <strong>{preview ?? "…"}</strong> clienti corrispondono alle regole
            </div>
            {segment?.kind && segment.kind !== "rules" && <p className="text-xs text-violet-700" data-testid="segment-product-note">Questo segmento nasce dall'analisi prodotti: le regole qui sopra si aggiungono alla sua lista clienti, che resta invariata. L'anteprima conta solo le regole.</p>}
          </div>
        )}
        <DialogFooter><Button data-testid="segment-save-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Salva segmento</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
